// apps/widget/src/screens/report.screen.tsx
//
// Staged reveal: score, then band, then radar, then cards, 60ms apart. Around
// 400ms total — enough to feel considered, not enough to annoy.
//
// One primary CTA. On this screen it is Book.

import { useEffect, useState } from 'react';
import { intlLocale, t } from '../lib/i18n';
import { prefersReducedMotion } from '../lib/theme';
import { useScan } from '../state';
import { ScoreBar } from '../components/score-bar';
import { ReportVisual } from '../components/report-visual';
import { RadarChart } from '../components/radar-chart';
import { MetricCard } from '../components/metric-card';
import { SkinTypeCard } from '../components/skin-type-card';
import { buildSummary } from '../lib/summary';
import { RecommendationsScreen } from './recommendations.screen';

export function ReportScreen() {
  const { state, go } = useScan();
  const report = state.report;
  const [revealed, setRevealed] = useState(prefersReducedMotion() ? 4 : 0);
  // The report and the clinic's treatments share the column, as two tabs, so
  // the photo stays beside both instead of the plan opening a separate screen.
  const [tab, setTab] = useState<'report' | 'treatments'>('report');
  // Weakest first, so the photo opens on what the plan is about to address.
  const [active, setActive] = useState<string | null>(
    () => [...(report?.metrics ?? [])].sort((a, b) => a.score - b.score)[0]?.key ?? null,
  );

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const timers = [1, 2, 3, 4].map((step) => setTimeout(() => setRevealed(step), step * 60 + 80));
    return () => timers.forEach(clearTimeout);
  }, []);

  if (!report) return null;

  // Worst first. The report opens on what needs attention, because that is what
  // the treatment plan below is about to address.
  // Every score is listed: each ring on the photo has a card to land on.
  const needsAttention = report.metrics.filter((m) => m.band !== 'good');
  const doingWell = report.metrics.filter((m) => m.band === 'good');

  return (
    // The same split as the opening screens: the report reads on the left,
    // this visitor's own scores sit on the right.
    <div className="gs-welcome gs-report">
      <div className="gs-welcome__copy gs-report__copy">
      <div className="gs-report-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'report'}
          className="gs-report-tab"
          onClick={() => setTab('report')}
        >
          {t('report.title')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'treatments'}
          className="gs-report-tab"
          onClick={() => setTab('treatments')}
        >
          {t('report.tabTreatments')}
        </button>
      </div>

      {tab === 'treatments' ? (
        <RecommendationsScreen />
      ) : (
      <>
      <div className="gs-reveal-step gs-report__head" data-shown={revealed >= 1 || undefined}>
        <h1 className="gs-report__title">{t('report.title')}</h1>
        <p className="gs-report__taken">
          {new Date(report.createdAt).toLocaleString(intlLocale(), {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </p>

        <div className="gs-report__figures">
          <span>
            {t('report.skinScore')} <b>{report.skinScore}</b>
          </span>
          <span>
            {/* Never presented as a measurement when it is a guess. */}
            {t('report.skinAge')}
            {report.skinAgeEstimated && <small> ({t('report.skinAgeEstimated')})</small>}{' '}
            <b>{report.skinAge}</b>
          </span>
        </div>

        {/* The verdict in words. The number alone does not say whether it is
            a good number. */}
        <p className="gs-report__verdict">
          {t('report.verdict', { band: t(`report.band.${report.band}`).toLowerCase() })}
        </p>

        <ScoreBar score={report.skinScore} band={report.band} />
      </div>

      {report.skinType && (
        <div className="gs-reveal-step" data-shown={revealed >= 2 || undefined}>
          <SkinTypeCard skinType={report.skinType} />
        </div>
      )}

      {/* Written from this scan's own scores, so no two visitors read the same
          paragraph, followed by the clinic's narrative when there is one. */}
      <div className="gs-reveal-step gs-summary" data-shown={revealed >= 2 || undefined}>
        <h2 className="gs-section-title">{t('report.summaryTitle')}</h2>
        {buildSummary(report).map((paragraph, i) => (
          <p key={i} className="gs-narrative">
            {paragraph}
          </p>
        ))}
        {/* The narrative is written once, to the patient, and stored. Staff see
            it quoted rather than reworded — it is what the patient read. */}
        {report.narrative &&
          (state.staffView ? (
            <figure className="gs-narrative gs-narrative--quoted">
              <figcaption className="gs-muted">{t('report.narrativeForPatient')}</figcaption>
              <blockquote>{report.narrative}</blockquote>
            </figure>
          ) : (
            <p className="gs-narrative">{report.narrative}</p>
          ))}
      </div>

      <div className="gs-reveal-step" data-shown={revealed >= 3 || undefined}>
        <RadarChart metrics={report.metrics} />
      </div>

      <div className="gs-reveal-step" data-shown={revealed >= 4 || undefined}>
        {needsAttention.length > 0 && (
          <>
            <h2 className="gs-section-title">{t('report.needsAttention')}</h2>
            <div className="gs-metric-list">
              {needsAttention.map((metric) => (
                <MetricCard
                  key={metric.key}
                  metric={metric}
                  active={metric.key === active}
                  onSelect={setActive}
                />
              ))}
            </div>
          </>
        )}

        {doingWell.length > 0 && (
          <>
            <h2 className="gs-section-title">{t('report.doingWell')}</h2>
            <div className="gs-metric-list">
              {doingWell.map((metric) => (
                <MetricCard
                  key={metric.key}
                  metric={metric}
                  compact
                  active={metric.key === active}
                  onSelect={setActive}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="gs-actions">
        <button className="gs-btn gs-btn--primary" onClick={() => setTab('treatments')}>
          {t('report.viewPlan')}
        </button>
        {/* The PDF is gated, the on-screen report is not — free report builds
            the trust that makes the gate acceptable. Not offered to staff
            viewing the report from their dashboard. */}
        {!state.staffView && (
          <button className="gs-btn gs-btn--ghost" onClick={() => go('lead')}>
            {t('report.emailReport')}
          </button>
        )}
      </div>
      </>
      )}
      </div>

      <ReportVisual report={report} active={active} onSelect={setActive} />
    </div>
  );
}
